import { logTokenEvent, setDiagnosticIdentity } from "@/api_framework/tokenDiagnostics";
import ModalPrompt from "@/app/components/ui/Modals/Prompt";
import { useOTPVerificationAPI } from "@/app/hooks/api_hooks/org/useOTPVerificationAPI";
import { useSendOTPAPI } from "@/app/hooks/api_hooks/org/useSendOTPAPI";
import { useUserRoleAPI } from "@/app/hooks/api_hooks/user/useUserRoleAPI";
import { useModalActions } from "@/app/hooks/ui_hooks/useModalActions";
import { useConfiguration } from "@/app/hooks/useConfiguration";
import { useRouter } from "@/app/hooks/useRouter";
import { useAppDispatch } from "@/app_redux/hooks/root_hook";
import { CheckCircleIcon } from "@heroicons/react/20/solid";
import { useEffect, useState } from "react";
import OTPInput from "react-otp-input";
import { useNavigate } from "react-router-dom";

import { signInWithCustomToken } from "firebase/auth";
import { auth } from "@/firebase"; // adjust path if needed
import toast from "react-hot-toast";

const LoginWithOTPModal: React.FC<{ className?: string }> = () => {
  const RESEND_COOLDOWN_SECONDS = 20;
  const { setInvisible } = useModalActions("LOGIN_WITH_OTP_MODAL");
  const { getRouteKey } = useRouter();
  const navigate = useNavigate();
  const configuration = useConfiguration();
  const dispatch = useAppDispatch();

  const [emailError, setEmailError] = useState<string>("");
  const [email, setEmail] = useState<string>("");
  const { execute: sendOTP } = useSendOTPAPI();
  const { execute: verifyOTP } = useOTPVerificationAPI();
  const getUserRole = useUserRoleAPI();

  const [otp, setOtp] = useState<string>("");
  const [otpSent, setOtpSent] = useState<boolean>(false);
  const [sendingOTP, setSendingOTP] = useState(false);
  const [verifyingOTP, setVerifyingOTP] = useState(false);
  const [resendCountdown, setResendCountdown] = useState(0);

  useEffect(() => {
    if (!otpSent || resendCountdown <= 0) return;

    const timeout = setTimeout(() => {
      setResendCountdown((previous) => previous - 1);
    }, 1000);

    return () => clearTimeout(timeout);
  }, [otpSent, resendCountdown]);

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;
    return emailRegex.test(email);
  };

  const handleSendOTP = async () => {
    if (sendingOTP) return;
    setSendingOTP(true);
    localStorage.removeItem("VMS_LOGOUT_EVENT");
    sessionStorage.clear();
    localStorage.clear();
    try {
      const response = await sendOTP(email, false);
      if (response) {
        setOtpSent(true);
        setOtp("");
        setResendCountdown(RESEND_COOLDOWN_SECONDS);
      }
    } finally {
      setSendingOTP(false);
    }
  };

  const handleEditEmail = () => {
    setOtpSent(false);
    setOtp("");
    setResendCountdown(0);
  };

  const handleLogin = async () => {
    localStorage.removeItem("VMS_LOGOUT_EVENT");
    await getUserRole(true);

    setOtpSent(false);
    setEmail("");
    setOtp("");
    setResendCountdown(0);
    setInvisible();
    navigate("/app/map-clinic-and-city");
  };

  // const handleVerifyOTP = async () => {
  //   localStorage.removeItem("VMS_LOGOUT_EVENT");
  //   await verifyOTP(email, otp).then((authToken) => {
  //     if (authToken) {
  //       const expiry = Date.now() + 12 * 60 * 60 * 1000; // 12 hours
  //       localStorage.setItem("sessionExpiry", expiry.toString());
  //       localStorage.setItem(configuration.localStorage.ACCESS_TOKEN.key, `Bearer ${authToken}`);
  //       handleLogin();
  //     } else {
  //       setOtp("");
  //     }
  //   });
  // };

  const handleVerifyOTP = async () => {
    try {
      localStorage.removeItem("VMS_LOGOUT_EVENT");
      setVerifyingOTP(true);

      const response: any = await verifyOTP(email, otp);

      if (!response?.auth_token) {
        setOtp("");
        setVerifyingOTP(false);
        return;
      }

      const { auth_token, firebase_token } = response;

      // 1️⃣ Store backend token
      const expiry = Date.now() + 12 * 60 * 60 * 1000;
      localStorage.setItem("sessionExpiry", expiry.toString());
      localStorage.setItem(configuration.localStorage.ACCESS_TOKEN.key, `Bearer ${auth_token}`);
      // Cache identity in memory so later diagnostics stay attributable even
      // if localStorage is wiped out from under us.
      setDiagnosticIdentity({ vms_user_id: email });
      logTokenEvent("USER_LOGIN", { method: "otp", email });
      logTokenEvent("ACCESS_TOKEN_ISSUED", { issued_by: "otp_login" });
      // 2️⃣ Firebase login
      await signInWithCustomToken(auth, firebase_token);

      // 3️⃣ Wait for Firebase auth state
      await new Promise<void>((resolve) => {
        const unsubscribe = auth.onAuthStateChanged((user) => {
          if (user) {
            unsubscribe();
            resolve();
          }
        });
      });

      // 4️⃣ Continue app login (fetch role + permissions)
      await handleLogin();
      setVerifyingOTP(false);

    } catch (error) {
      setVerifyingOTP(false);
      console.error("OTP verification failed", error);
      toast.error("OTP verification failed");
    }
  };

  const handleEmailChange = (e?: React.ChangeEvent<HTMLInputElement>) => {
    const newEmail = e.target.value;
    setEmail(newEmail);

    if (e.target.value === "") {
      setEmailError("");
      return;
    }
    if (validateEmail(newEmail)) {
      setEmailError("");
    } else {
      setEmailError("Invalid email format");
    }
  };

  return (
    <ModalPrompt
      modalId="LOGIN_WITH_OTP_MODAL"
      persistent
      button={{
        primaryButtonText: !otpSent
          ? sendingOTP
            ? "Sending..."
            : "Send OTP"
          : verifyingOTP
            ? "Verifying..."
            : "Verify OTP",
        secondaryButtonText: "Cancel",
        primaryCallback: () => {
          if (!otpSent) {
            handleSendOTP();
          } else {
            handleVerifyOTP();
          }
        },
        primaryButtonDisabled: !otpSent
          ? email === "" || emailError !== "" || sendingOTP
          : otp.length !== 4 || verifyingOTP,
        primaryButtonClassname: "bg-black text-white",
        secondaryCallback: () => {
          setOtpSent(false);
          setEmail("");
          setOtp("");
          setResendCountdown(0);
          setInvisible();
        },
        shouldHideOnClick: false,
      }}
      title="Login With OTP"
      content={
        <div className="">
          <div className="relative">
            <input
              className="w-full h-10 rounded-md border border-gray-300 p-2 disabled:opacity-50 disabled:cursor-not-allowed disabled:bg-gray-100"
              type="text"
              value={email}
              disabled={otpSent}
              onChange={(e) => {
                handleEmailChange(e);
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter" && email !== "" && emailError === "" && !sendingOTP) {
                  e.preventDefault();
                  handleSendOTP();
                }
              }}
              placeholder="Enter your email for OTP"
            />
            {emailError && <p className="text-red-500 text-sm absolute top-full mt-1">{emailError}</p>}
          </div>

          {otpSent && (
            <div className="flex items-center justify-between mt-2 mb-2 gap-x-3">
              <p className="flex items-center gap-x-2 text-sm">
                <CheckCircleIcon className="w-4 h-4 text-green-500" /> OTP Sent to {email}
              </p>
              <button type="button" className="text-sm text-blue-600 hover:text-blue-700" onClick={handleEditEmail}>
                Edit Email
              </button>
            </div>
          )}
          {otpSent && (
            <div className="flex flex-col gap-y-2 mt-8 mb-4">
              <p>Please enter the OTP sent to your email</p>
              <OTPInput
                containerStyle={{
                  width: "100%",
                  justifyContent: "center",
                  alignItems: "center",
                  display: "flex",
                  gap: "40px",
                }}
                inputStyle={{ width: "100%", height: "100%", borderRadius: "10px", border: "1px solid #ccc" }}
                value={otp}
                onChange={setOtp}
                numInputs={4}
                renderInput={(props) => (
                  <input
                    {...props}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && otp.length === 4) {
                        handleVerifyOTP();
                      }
                    }}
                  />
                )}
              />
              <div className="flex items-center justify-between text-sm mt-1">
                <button
                  type="button"
                  className="text-blue-600 disabled:text-gray-400 cursor-pointer"
                  onClick={handleSendOTP}
                  disabled={resendCountdown > 0 || sendingOTP}
                >
                  {sendingOTP ? "Resending..." : "Resend OTP"}
                </button>
                <span className="text-gray-500">{resendCountdown > 0 ? `Resend in ${resendCountdown}s` : ""}</span>
              </div>
            </div>
          )}
        </div>
      }
    />
  );
};

export default LoginWithOTPModal;
